import React, { useState, useEffect, memo, useMemo, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { UserButton } from "@clerk/clerk-react";
import { 
  FaHome, FaUsers, FaCar, FaTools, FaCog, FaCalendarAlt, 
  FaMapMarkerAlt, FaClipboardList, FaWrench, FaWarehouse, 
  FaUserCog, FaChartBar, FaFileInvoiceDollar, FaSignOutAlt,
  FaBars, FaTimes, FaEllipsisH, FaShoppingCart
} from 'react-icons/fa';
import CustomSelect from '@/components/common/CustomSelect';
import { useAuth } from '@/context/AuthContextApi';
import { useSucursal } from '@/context/SucursalContext';
import { useSimpleEmpresa } from '@/hooks/useSimpleEmpresa';
import * as sucursalesApiService from '@/services/sucursalesApiService';
import Logo from '@/components/common/Logo';
import SimplePlanIndicator from '@/components/sidebar/SimplePlanIndicator';
import PaymentModal from '@/components/payments/PaymentModal';
import revvlyLogo from '@/img/revvly.png';
import '@/styles/SidebarStyles.css';

/**
 * Componente Sidebar mejorado con correcciones para la versión móvil
 * - Indicador visual claro para el ítem seleccionado en móvil
 * - Sin espacio entre barra y menú desplegable
 * 
 * @returns {JSX.Element} - Componente Sidebar
 */
const Sidebar = () => {
  const [sucursales, setSucursales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  
  // Estados del modal de pago - movidos aquí para que persistan
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [targetPlanId, setTargetPlanId] = useState(null);
  
  // Hook para manejar múltiples instancias
  
  const location = useLocation();
  
  // Log después de inicializar location (comentado para reducir spam)
  // console.log('📋 [SIDEBAR] Component rendering - timestamp:', new Date().toLocaleTimeString(), 'location:', location.pathname);
  // Optimizar useAuth también
  const authContext = useAuth();
  const logout = useCallback(() => authContext.logout(), [authContext.logout]);
  const user = useMemo(() => authContext.user, [authContext.user?.id]);
  // Usar sucursal context directamente sin optimizaciones complejas
  const sucursalContext = useSucursal();
  const { 
    selectedSucursal, 
    setSucursal, 
    updateSucursalesCache, 
    getSucursalesFromCache, 
    isCacheValid, 
    clearSucursalesCache 
  } = sucursalContext;
  // Usar SimpleEmpresa simplificado
  const { 
    isSuperAdmin, 
    isAdmin, 
    empresaActual, 
    empresasDisponibles, 
    cambiarEmpresa, 
    loading: empresaLoading 
  } = useSimpleEmpresa();
  
  // Detectar si estamos en móvil
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    // Comprobar inicialmente
    checkMobile();
    
    // Usar event listener seguro para múltiples instancias
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // NUEVA LÓGICA: Sidebar como fuente de verdad para sucursales
  useEffect(() => {
    console.log('🏢 [Sidebar] useEffect para sucursales - empresaActual:', empresaActual?.id);
    const fetchSucursales = async () => {
      // Si cambió la empresa, limpiar caché
      if (empresaActual?.id) {
        console.log('🔄 [Sidebar] Cargando sucursales para empresa:', empresaActual.id);
        // Verificar si tenemos datos válidos en cache para esta empresa
        const cachedData = getSucursalesFromCache();
        if (isCacheValid() && cachedData.length > 0) {
          //console.log('📦 [Sidebar] Usando sucursales desde contexto cache');
          setSucursales(cachedData);
          
          // Si no hay sucursal seleccionada, seleccionar la primera
          if ((!selectedSucursal || !cachedData.find(s => s.id === selectedSucursal)) && cachedData.length > 0) {
            console.log('✅ [Sidebar] Auto-seleccionando primera sucursal del cache:', cachedData[0].Nombre);
            console.log('🔧 [Sidebar] Ejecutando setSucursal con:', { id: cachedData[0].id, data: cachedData[0] });
            setSucursal(cachedData[0].id, cachedData[0]);
            console.log('✅ [Sidebar] setSucursal ejecutado');
          }
          return;
        }
        
        // No hay cache válido, hacer llamada API
        setLoading(true);
        try {
          //console.log('🔄 [Sidebar] Cargando sucursales desde API para empresa:', empresaActual.id);
          const data = await sucursalesApiService.getSucursales(empresaActual.id);
          
          // Actualizar estado local y cache del contexto
          setSucursales(data);
          updateSucursalesCache(data);
          
          // Si no hay sucursal seleccionada, seleccionar la primera de esta empresa
          if ((!selectedSucursal || !data.find(s => s.id === selectedSucursal)) && data.length > 0) {
            console.log('✅ [Sidebar] Auto-seleccionando primera sucursal de API:', data[0].Nombre);
            setSucursal(data[0].id, data[0]);
          }
        } catch (error) {
          console.error('❌ [Sidebar] Error cargando sucursales:', error);
        } finally {
          setLoading(false);
        }
      } 
      // Para usuarios sin empresa seleccionada, cargar todas las sucursales (fallback)
      else {
        const cachedData = getSucursalesFromCache();
        if (isCacheValid() && cachedData.length > 0) {
          //console.log('📦 [Sidebar] Usando sucursales desde contexto cache (sin empresa)');
          setSucursales(cachedData);
          
          if (!selectedSucursal && cachedData.length > 0) {
            console.log('✅ [Sidebar] Auto-seleccionando primera sucursal (sin empresa):', cachedData[0].Nombre);
            setSucursal(cachedData[0].id, cachedData[0]);
          }
          return;
        }
        
        setLoading(true);
        try {
          //console.log('🔄 [Sidebar] Cargando todas las sucursales desde API');
          const data = await sucursalesApiService.getSucursales();
          
          setSucursales(data);
          updateSucursalesCache(data);
          
          if (!selectedSucursal && data.length > 0) {
            setSucursal(data[0].id, data[0]);
          }
        } catch (error) {
          console.error('❌ [Sidebar] Error cargando sucursales:', error);
        } finally {
          setLoading(false);
        }
      }
    };
    
    fetchSucursales();
  }, [empresaActual?.id]); // Simplificar dependencias para evitar re-renders
  

  
  // Manejar cambio de empresa (solo para SuperAdmin) - memoizado
  const handleEmpresaChange = useCallback((e) => {
    const empresaId = parseInt(e.target.value);
    
    const empresaSeleccionada = empresasDisponibles.find(emp => emp.id === empresaId);
    
    if (empresaSeleccionada) {
      // IMPORTANTE: Limpiar caché de sucursales al cambiar empresa
      clearSucursalesCache();
      //console.log('🗑️ [Sidebar] Cache de sucursales limpiado por cambio de empresa');
      
      cambiarEmpresa(empresaId);
      
      // Limpiar sucursal seleccionada al cambiar empresa
      setSucursal('', null);
    }
  }, [empresasDisponibles, clearSucursalesCache, cambiarEmpresa, setSucursal]);

  // Manejar cambio de sucursal - memoizado
  const handleSucursalChange = useCallback((e) => {
    const sucursalId = e.target.value;
    
    // Convertir a número para comparación correcta
    const sucursalIdNum = parseInt(sucursalId);
    const sucursalSeleccionada = sucursales.find(s => s.id === sucursalIdNum);
    
    
    // Actualizar tanto el ID como los datos completos de la sucursal
    if (sucursalSeleccionada) {
      setSucursal(sucursalIdNum, sucursalSeleccionada);
      
      // Guardar en localStorage para persistencia
      localStorage.setItem('selectedSucursal', sucursalIdNum.toString());
      localStorage.setItem('sucursalData', JSON.stringify(sucursalSeleccionada));
      
    } else {
    }
  }, [sucursales, setSucursal]);
  
  // Obtener nombre de la sucursal seleccionada - memoizado
  const getSelectedSucursalName = useMemo(() => {
    const sucursal = sucursales.find(s => s.id === selectedSucursal);
    return sucursal ? sucursal.Nombre : 'Seleccionar sucursal';
  }, [sucursales, selectedSucursal]);
  
  // Manejar cierre de sesión - memoizado
  const handleLogout = useCallback(async () => {
    try {
      await logout();
    } catch (error) {
    }
  }, [logout]);
  
  // Callback para abrir modal desde PlanIndicator
  const handleModalOpen = useCallback((targetId) => {
    setMoreMenuOpen(false);
    setTargetPlanId(targetId);
    
    // Delay para móvil para que se cierre el menú primero
    setTimeout(() => {
      setShowPaymentModal(true);
    }, isMobile ? 300 : 0);
  }, [isMobile]);
  
  // Links de navegación - memoizado
  const navLinks = useMemo(() => [
    {
      to: '/dashboard',
      icon: <FaHome />,
      text: 'Inicio',
      adminOnly: false
    },
    {
      to: '/clientes',
      icon: <FaUsers />,
      text: 'Clientes',
      adminOnly: true
    },
    {
      to: '/vehiculos',
      icon: <FaCar />,
      text: 'Vehículos',
      adminOnly: false
    },
    {
      to: '/citas',
      icon: <FaCalendarAlt />,
      text: 'Citas',
      adminOnly: false
    },
    {
      to: '/mantenimientos',
      icon: <FaWrench />,
      text: 'Mantenimientos',
      adminOnly: false
    },
    {
      to: '/servicios',
      icon: <FaTools />,
      text: 'Servicios',
      adminOnly: true
    },
    {
      to: '/repuestos',
      icon: <FaWarehouse />,
      text: 'Repuestos',
      adminOnly: true
    },
    {
      to: '/marketplace-repuestos',
      icon: <FaShoppingCart />,
      text: 'Marketplace',
      adminOnly: true
    },
    {
      to: '/mecanicos',
      icon: <FaUserCog />,
      text: 'Mecánicos',
      adminOnly: true
    },
    {
      to: '/configuracion/general',
      icon: <FaCog />,
      text: 'Configuración',
      adminOnly: true
    }
  ], []);
  
  // Filtrar enlaces según rol - SuperAdmin ve todo, isAdmin ve solo admin, otros ven solo público - memoizado
  const filteredLinks = useMemo(() => {
    // Verificación más robusta: esperar a que el contexto esté inicializado
    const contextLoaded = empresaActual !== null || !empresaLoading;
    
    // Si aún no está cargado, mostrar todos los enlaces para evitar parpadeo
    // Esto permite que el usuario vea el menú completo mientras se carga el contexto
    if (!contextLoaded) {
      console.log('🔗 [Sidebar] Contexto aún cargando, mostrando todos los enlaces temporalmente');
      return navLinks;
    }
    
    const filtered = navLinks.filter(link => !link.adminOnly || isAdmin || isSuperAdmin);
    console.log('🔗 [Sidebar] Filtered links updated:', { 
      total: filtered.length, 
      isAdmin, 
      isSuperAdmin, 
      currentPath: location.pathname,
      contextLoaded,
      empresaActual: !!empresaActual
    });
    return filtered;
  }, [navLinks, isAdmin, isSuperAdmin, location.pathname, empresaActual, empresaLoading]);
  
  // Usar los mismos enlaces filtrados para móvil
  const mobileFilteredLinks = filteredLinks;
  
  // Renderizar versión escritorio (mantiene tu diseño original)
  const DesktopSidebar = useMemo(() => () => (
    <div className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:flex lg:flex-col w-64 bg-[#192231] text-white z-40" 
         style={{
           height: '125vh',
           minHeight: '125vh',
           transform: 'scale(0.8)',
           transformOrigin: 'top left'
         }}>
      {/* Logo y Panel de Administración */}
      <div className="p-4 ">
        <div className="flex items-center justify-center mb-4">
          <Logo height={56} className="mb-2" />
        </div>
        <div className="bg-[#262F45] rounded-lg p-3 flex justify-between items-center">
          <div className="flex items-center">
            <div className="mr-3" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <UserButton afterSignOutUrl="/taller" />
            </div>
            <div>
              <div className="text-blue-400 text-sm font-medium">Usuario</div>
              <div className="text-white font-semibold">{user?.Nombre || 'admin'}</div>
            </div>
          </div>
          {/* Botón de cerrar sesión como círculo rojo */}
          <button
            onClick={handleLogout}
            className="w-10 h-10 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center transition-colors"
            title="Cerrar sesión"
          >
            <FaSignOutAlt className="text-white text-base" />
          </button>
        </div>
      </div>
      {/* Selector de empresa (solo para SuperAdmin o usuarios multi-empresa) */}
      {(isSuperAdmin || empresasDisponibles.length > 1) && (
        <div className="mb-1">
          <div className="flex items-center px-4 py-2 rounded transition-colors text-white hover:bg-[#262F45]">
            <div className="w-full">
              <label className="text-sm font-medium text-gray-300 block mb-1">Empresa:</label>
              <CustomSelect
                value={empresaActual?.id || ''}
                onChange={handleEmpresaChange}
                placeholder="Seleccionar empresa"
                options={empresasDisponibles.map(empresa => ({
                  id: empresa.id,
                  value: empresa.id,
                  label: empresa.nombre
                }))}
                className="w-full"
                disabled={empresaLoading || (!isSuperAdmin && empresasDisponibles.length <= 1)}
              />
            </div>
          </div>
        </div>
      )}
      
      {/* Indicador de plan */}
      <SimplePlanIndicator onModalOpen={handleModalOpen} />

      {/* Selector de sucursal */}
      <div className="mb-1">
        <div className="px-4 py-2 rounded transition-colors text-white hover:bg-[#262F45]">
          <label className="text-sm font-medium text-gray-300 block mb-1">
            {isSuperAdmin ? 'Sucursal:' : 'Seleccionar Sucursal:'}
          </label>
          <CustomSelect
            value={selectedSucursal || ''}
            onChange={handleSucursalChange}
            placeholder={isSuperAdmin && !empresaActual ? 'Seleccione empresa primero' : 'Seleccionar sucursal'}
            options={sucursales.map(sucursal => ({
              id: sucursal.id,
              value: sucursal.id,
              label: sucursal.Nombre
            }))}
            className="w-full"
            disabled={loading || (isSuperAdmin && !empresaActual)}
          />
        </div>
      </div>
      
      {/* Navegación */}
      <nav className="flex-1 py-4 overflow-y-auto">
        <ul>
          {filteredLinks.map(link => {
            const isActive = location.pathname === link.to ||
              (link.to !== '/' && location.pathname.startsWith(link.to));
            return (
              <li key={link.to} className="mb-1">
                <Link
                  to={link.to}
                  className={`flex items-center px-4 py-2 rounded transition-colors ${
                    isActive
                      ? 'bg-blue-600 text-white'
                      : 'text-white hover:bg-[#262F45]'
                  }`}
                >
                  <span className="mr-3">{link.icon}</span>
                  {link.text}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
      
      {/* Branding - Powered by */}
      <div className="px-4 pb-2 mt-auto">
        <div className="text-center">
          <div className="flex justify-center mb-2">
            <img 
              src={revvlyLogo} 
              alt="Revvly" 
              style={{ height: 32, width: 'auto' }}
              className="object-contain"
            />
          </div>
          <div className="text-gray-400 text-xs">Powered by Aitomata</div>
        </div>
      </div>
    </div>
  ), [filteredLinks, isSuperAdmin, empresaActual?.id, selectedSucursal, loading, empresasDisponibles.length, location.pathname]);
  
  // Renderizar versión móvil CORREGIDA
  const MobileSidebar = useMemo(() => () => (
    <div className="mobile-sidebar lg:hidden fixed bottom-0 left-0 right-0 bg-gray-800 z-50">
      
      {/* Menú principal en la parte inferior */}
      <div className="grid grid-cols-5 h-16 border-t border-gray-700 bg-[#1a2234] shadow-lg" style={{ height: '64px', position: 'relative', width: '100%' }}>
        {mobileFilteredLinks.slice(0, 4).map(link => {
          // Obtener la URL completa actual
          const isActive = location.pathname === link.to ||
              (link.to !== '/' && location.pathname.startsWith(link.to));
            
          return (
            <Link
              key={link.to}
              to={link.to}
              className={`flex flex-col justify-center items-center mobile-nav-button ${
                isActive ? 'text-blue-400 font-semibold' : 'text-gray-400 hover:text-white'
              }`}
              style={isActive ? {
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                borderTop: '2px solid #3B82F6'
              } : {}}
            >
              <span className={`text-xl ${isActive ? 'text-blue-400' : ''}`}>{link.icon}</span>
              <span className={`text-xs mt-1 ${isActive ? 'text-blue-400' : ''}`}>{link.text}</span>
            </Link>
          );
        })}
        
        {/* Botón "Más" que abre un menú con el resto de opciones y el selector de sucursal */}
        <button
          onClick={() => setMoreMenuOpen(!moreMenuOpen)}
          className={`flex flex-col justify-center items-center mobile-nav-button ${
            moreMenuOpen ? 'text-blue-400 font-semibold bg-blue-900 bg-opacity-20' : 'text-gray-400 hover:text-white'
          }`}
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            zIndex: 50, // Mayor que el menú contextual (z-40) para que se vea por encima
            ...(moreMenuOpen ? { borderTop: '2px solid #3B82F6' } : {})
          }}
        >
          <span className="text-xl" style={{ fontSize: '1.25rem', marginBottom: '2px' }}>
            {moreMenuOpen ? <FaTimes className="text-red-900" /> : <FaEllipsisH />}
          </span>
          <span className="text-xs" style={{ fontSize: '0.75rem', marginTop: '2px' }}>Más</span>
        </button>
      </div>
      
      {/* Menú expandido "Más" - CORREGIDO: Eliminamos el espacio entre barra y menú */}
      {moreMenuOpen && (
        <>
          {/* Overlay para cerrar al tocar fuera */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setMoreMenuOpen(false)}
          ></div>
          
          {/* Menú desplegable - Diseño mejorado */}
          <div className="mobile-more-menu fixed left-0 right-0 z-50" 
               style={{
                 backdropFilter: 'blur(10px)', 
                 background: 'rgba(26,34,52,0.98)', 
                 boxShadow: 'none',
                 padding: '8px',
                 paddingBottom: '0',
                 bottom: '64px', // Posicionamos justo encima de la barra de navegación
                 left: '0',
                 right: '0',
                 zIndex: 41, // Mayor que el overlay
                 borderBottom: 'none'
               }}>
            {/* Avatar/datos y cerrar sesión en la misma tarjeta */}
            <div style={{
              backgroundColor: '#26324a',
              borderRadius: '0.5rem',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              marginBottom: '12px',
              padding: '16px',
              display: 'flex',
              alignItems: 'center'
            }}>
              {/* Avatar y datos - ocupa 2/3 del espacio */}
              <div className="flex items-center w-2/3">
                <div style={{
                  marginRight: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <UserButton afterSignOutUrl="/taller" />                  
                </div>
                <div>
                  <div style={{
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    fontWeight: 500,
                    color: '#60a5fa',
                    fontSize: '0.75rem'
                  }}>USUARIO</div>
                  <div style={{
                    fontWeight: 700,
                    color: 'white',
                    fontSize: '0.875rem'
                  }}>{user?.Nombre || 'admin'}</div>
                </div>
              </div>
              {/* Botón cerrar sesión - ocupa 1/3 del espacio */}
              <div className="w-1/3 flex justify-end">
                <button
                  onClick={() => {
                    handleLogout();
                    setMoreMenuOpen(false);
                  }}
                  className="w-full flex flex-col items-center justify-center px-2 py-2 rounded-lg bg-red-800 text-white text-xs font-medium shadow-md hover:bg-red-600 transition-colors"
                >
                  <FaSignOutAlt className="text-base mb-1" />
                  <span className="font-medium text-xs">Cerrar sesión</span>
                </button>
              </div>
            </div>

            {/* Selector de empresa (solo para SuperAdmin o usuarios multi-empresa) */}
            {(isSuperAdmin || empresasDisponibles.length > 1) && (
              <div className="empresa-container mb-3">
                <div className="flex items-center mb-2">
                  <div style={{
                    backgroundColor: 'rgba(59, 130, 246, 0.2)',
                    padding: '6px',
                    borderRadius: '0.375rem',
                    marginRight: '8px'
                  }}>
                    <FaWarehouse style={{ color: '#60a5fa', fontSize: '1.125rem' }} />
                  </div>
                  <label style={{
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    fontWeight: 500,
                    color: '#60a5fa',
                    fontSize: '0.75rem'
                  }}>EMPRESA</label>
                  <span style={{
                    marginLeft: 'auto',
                    fontSize: '0.75rem',
                    color: '#d1d5db',
                    fontWeight: 500
                  }}>{empresaActual?.nombre || 'Sin seleccionar'}</span>
                </div>
                {isMobile ? (
                  <CustomSelect
                    className="w-full p-2 mb-2 rounded-lg bg-[#1e293b] text-white border border-gray-700"
                    value={empresaActual?.id || ''}
                    onChange={handleEmpresaChange}
                    disabled={empresaLoading || (!isSuperAdmin && empresasDisponibles.length <= 1)}
                    placeholder="Seleccionar empresa"
                    options={empresasDisponibles.map(empresa => ({
                      id: empresa.id,
                      label: empresa.nombre
                    }))}
                  />
                ) : (
                  <select
                    className="w-full p-2 rounded-lg bg-gray-700 border border-gray-600 text-white"
                    value={empresaActual?.id || ''}
                    onChange={handleEmpresaChange}
                    disabled={empresaLoading || (!isSuperAdmin && empresasDisponibles.length <= 1)}
                  >
                    <option value="">Seleccionar empresa</option>
                    {empresasDisponibles.map(empresa => (
                      <option key={empresa.id} value={empresa.id}>
                        {empresa.nombre}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {/* Plan y Selector de sucursal en la misma fila para móvil */}
            <div className="grid grid-cols-2 gap-3 mb-3">
              {/* Indicador de plan para móvil - ocupa 1/3 del espacio */}
              <div className="plan-container">
                <SimplePlanIndicator isMobile={true} onModalOpen={handleModalOpen} />
              </div>

              {/* Selector de sucursal - ocupa 2/3 del espacio */}
              <div className="sucursal-container">
                <div className="flex items-center mb-2">
                  <div style={{
                    backgroundColor: 'rgba(59, 130, 246, 0.2)',
                    padding: '6px',
                    borderRadius: '0.375rem',
                    marginRight: '8px'
                  }}>
                    <FaMapMarkerAlt style={{ color: '#60a5fa', fontSize: '1.125rem' }} />
                  </div>
                  <label style={{
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    fontWeight: 500,
                    color: '#60a5fa',
                    fontSize: '0.75rem'
                  }}>SUCURSAL</label>
                </div>
                {isMobile ? (
                  <CustomSelect
                    className="w-full p-2 mb-2 rounded-lg bg-[#1e293b] text-white border border-gray-700"
                    value={selectedSucursal || ''}
                    onChange={handleSucursalChange}
                    disabled={loading || (isSuperAdmin && !empresaActual)}
                    placeholder={isSuperAdmin && !empresaActual ? 'Seleccione empresa primero' : 'Seleccionar sucursal'}
                    options={sucursales.map(sucursal => ({
                      id: sucursal.id,
                      label: sucursal.Nombre
                    }))}
                  />
                ) : (
                  <select
                    className="w-full p-2 rounded-lg bg-gray-700 border border-gray-600 text-white"
                    value={selectedSucursal || ''}
                    onChange={handleSucursalChange}
                    disabled={loading || (isSuperAdmin && !empresaActual)}
                  >
                    <option value="">
                      {isSuperAdmin && !empresaActual ? 'Seleccione empresa primero' : 'Seleccionar sucursal'}
                    </option>
                    {sucursales.map(sucursal => (
                      <option key={sucursal.id} value={sucursal.id}>
                        {sucursal.Nombre}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            {/* Enlaces adicionales en 2 columnas - para administradores y SuperAdmin */}
            {(isAdmin || isSuperAdmin) && (
              <div className="grid grid-cols-2 gap-3 mb-1" style={{ maxHeight: 'none', overflow: 'visible' }}>
                {navLinks.slice(4, 10).map(link => {
                  // Obtener la URL completa actual
                  const isActive = location.pathname === link.to ||
                      (link.to !== '/' && location.pathname.startsWith(link.to));

                  const textStyle = {
                    color: isActive ? 'white' : 'inherit'
                  };
                  
                  return (
                    <Link
                      key={link.to}
                      to={link.to}
                      className={`flex flex-col items-center justify-center px-2 py-4 rounded-lg text-base transition-colors shadow-md text-center ${
                        isActive ? 'text-blue-400 font-semibold bg-blue-500' : 'text-gray-400 hover:text-white bg-[#26324a]'
                      }`}
                      onClick={() => setMoreMenuOpen(false)}
                    >
                      <span className="text-xl mb-1.5" style={textStyle}>
                        {link.icon}
                      </span>
                      <span className="text-xs font-medium" style={textStyle}>
                        {link.text}
                      </span>
                    </Link>
                  );
                })}
              </div>
            )}
            
            {/* Branding - Logos y Powered by */}
            <div className="mt-4 pt-3 border-t border-gray-600">
              <div className="text-center">
                {/* Logos lado a lado */}
                <div className="flex justify-center items-center gap-4 mb-2">
                  <Logo height={32} />
                  <img 
                    src={revvlyLogo} 
                    alt="Revvly" 
                    style={{ height: 28, width: 'auto' }}
                    className="object-contain"
                  />
                </div>
                <div className="text-gray-400 text-xs">Powered by Aitomata</div>
              </div>
            </div>
          </div>

          {/* Animación fade/slide */}
          <style>{`
            @keyframes fadeInUp {
              from { opacity: 0; transform: translateY(24px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}</style>
        </>
      )}
    </div>
  ), [mobileFilteredLinks, moreMenuOpen, selectedSucursal, loading, isSuperAdmin, empresaActual?.id, sucursales.length, location.pathname]);
  
  return (
    <>
      <div className="hidden lg:block">
        <DesktopSidebar />
      </div>
      <div className="block lg:hidden">
        <MobileSidebar />
      </div>
      
      {/* Modal de pago global */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        targetPlanId={targetPlanId}
      />
    </>
  );
};

export default memo(Sidebar);